import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Link } from 'react-router';
import { formatCents } from '@crave/shared';
import { ApiError } from '@crave/api-client';
import { Button, Spicy } from '@crave/ui';
import { api } from '../api.ts';
import { cart, cartSubtotalCents, lineTotalCents, useCart, useOrderLocation } from '../lib/cart.ts';
import type { Quote } from '../lib/types.ts';
import { useMenu } from './MenuPage.tsx';

export function CartPage() {
  const lines = useCart();
  const locationSlug = useOrderLocation();
  const menu = useMenu();
  const location = menu.data?.location;
  const open = location ? location.ordering_enabled && !location.ordering_paused : false;
  const pricesShown = location ? location.show_prices !== false : false;
  // Every real price is above zero; a line added while prices were hidden has none stored.
  const storedPricesKnown = lines.every((l) => l.unit_price_cents > 0);
  /**
   * When the menu hides prices (or a stored price is missing), the cart asks the API for the real
   * amounts while ordering is open, so the customer always sees what they will pay. When ordering
   * is closed and prices are hidden, no price shows anywhere.
   */
  const needQuote = open && (!pricesShown || !storedPricesKnown);
  const itemsBody = useMemo(
    () =>
      lines.map((l) => ({
        menu_item_id: l.menu_item_id,
        quantity: l.quantity,
        modifier_ids: l.modifier_ids,
      })),
    [lines],
  );
  const quote = useQuery({
    queryKey: ['cart-quote', locationSlug, itemsBody],
    queryFn: async () =>
      (
        await api.post<Quote>(`/public/locations/${locationSlug}/quote`, {
          items: itemsBody,
          tip_cents: 0,
        })
      ).data,
    enabled: needQuote && lines.length > 0,
  });
  const quoted = needQuote && !quote.isError ? quote.data : undefined;
  const localPrices = pricesShown && storedPricesKnown;

  if (lines.length === 0)
    return (
      <div>
        <p>Your cart is empty.</p>
        <Link to="/">Back to the menu</Link>
      </div>
    );

  const lineAmount = (index: number, fallback: number): string | null => {
    const q = quoted?.lines[index];
    if (q) return formatCents(q.line_total_cents);
    return localPrices ? formatCents(fallback) : null;
  };
  const subtotal = quoted
    ? formatCents(quoted.totals.subtotal_cents)
    : localPrices
      ? formatCents(cartSubtotalCents(lines))
      : null;
  const quoteProblem =
    needQuote && quote.isError
      ? quote.error instanceof ApiError
        ? quote.error.message
        : 'We could not get your total. Please try again.'
      : null;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <h1 style={{ margin: 0 }}>Your cart</h1>
      {lines.map((l, i) => {
        const amount = lineAmount(i, lineTotalCents(l));
        return (
          <div
            key={l.key}
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              gap: 8,
              padding: '12px 0',
              borderBottom: '1px solid var(--line)',
            }}
          >
            <div>
              <strong>{l.name}</strong>
              {l.spicy && (
                <>
                  {' '}
                  <Spicy size="sm" />
                </>
              )}
              {l.modifier_names.length > 0 && (
                <div style={{ fontSize: 14, color: 'var(--ink-soft)' }}>
                  {l.modifier_names.join(', ')}
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                <Button
                  variant="secondary"
                  aria-label="Fewer"
                  onClick={() => cart.setQuantity(l.key, l.quantity - 1)}
                >
                  −
                </Button>
                <span style={{ fontWeight: 700, minWidth: 20, textAlign: 'center' }}>
                  {l.quantity}
                </span>
                <Button
                  variant="secondary"
                  aria-label="More"
                  onClick={() => cart.setQuantity(l.key, Math.min(20, l.quantity + 1))}
                >
                  +
                </Button>
              </div>
            </div>
            {amount !== null && <strong>{amount}</strong>}
          </div>
        );
      })}
      {quoteProblem && (
        <p role="alert" style={{ margin: 0, color: 'var(--crave-red)' }}>
          {quoteProblem}
        </p>
      )}
      {subtotal !== null ? (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
            <span>Subtotal</span>
            <span>{subtotal}</span>
          </div>
          <p style={{ margin: 0, color: 'var(--ink-soft)', fontSize: 14 }}>
            Tax is added at checkout. No fees, ever.
          </p>
        </>
      ) : (
        needQuote &&
        !quoteProblem && (
          <p style={{ margin: 0, color: 'var(--ink-soft)', fontSize: 14 }}>Getting your total…</p>
        )
      )}
      <Link to="/checkout" style={{ textDecoration: 'none' }}>
        <Button size="lg" style={{ width: '100%' }}>
          Choose pickup time
        </Button>
      </Link>
      <Link
        to="/"
        style={{
          display: 'block',
          padding: '10px 0',
          textAlign: 'center',
          color: 'var(--ink)',
          fontWeight: 600,
          textUnderlineOffset: 3,
        }}
      >
        Add more
      </Link>
    </div>
  );
}
